import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import type { SSNotification } from "@keystone-ui/types";

import { useEligibleOffers } from "components/offers/hooks";
import {
  usePolicyId,
  useMutateNotificationUiEvents,
  useUser,
  useUiEvents,
  type NotificationsStateUpdate,
  useSSNotifications,
} from "data/ss-data-hooks";
import { trackEvent, useFlags } from "utils";

import { notificationContent, offersDeclinedMap } from "./content";

/**
 * Pulls server-side notifications and frontend derived notifications, state
 * from ui-events together. `isLoading` stays true until read/dismissed state
 * and offers have loaded; an offers error only drops the offer notifications.
 */
export function useNotificationsData() {
  const selectedPolicyId = usePolicyId();
  const user = useUser();
  const {
    eligibleOffers,
    loading: offersLoading,
    error: offersError,
  } = useEligibleOffers(selectedPolicyId);
  const { data: uiEvents, isLoading: uiEventsLoading } = useUiEvents();
  const ssNotificationData = useSSNotifications();

  const isLoading = uiEventsLoading || (offersLoading && !offersError);

  const notifications = useMemo(() => {
    const state: NotificationsStateUpdate = { read: [], dismissed: [] };
    if (!uiEventsLoading) {
      const savedState = uiEvents?.events?.notifications as
        | NotificationsStateUpdate
        | undefined;
      state.read = savedState?.read ?? [];
      state.dismissed = savedState?.dismissed ?? [];
    }

    // Update read and dismissed for server-side notifications
    const output = (ssNotificationData ?? [])
      .filter((n) => {
        return !state.dismissed.includes(n.id);
      })
      .map((n) => ({ ...n, read: state.read.includes(n.id) }));

    // Check for marketing opt-in pref. If not set, add notification.
    const showOptIn =
      !user?.accountPreferences.find(
        (pref) => pref.preferenceName === "marketing-optin",
      )?.preferenceValue && !state.dismissed.includes("marketing-optin");
    if (showOptIn) {
      const content = notificationContent["marketing-optin"];
      output.push({
        id: "marketing-optin",
        title: content.title,
        description: content.description,
        policyIds: [],
        actionLabel: content.buttonText,
        actionUrl: content.path,
        read: state.read.includes("marketing-optin"),
      });
    }

    // Check offers prefs, map to notification
    const offerNotifications = (
      offersError ? [] : eligibleOffers.unenrolledOffers
    )
      .map((offer) => offer.offeringInternalName)
      .filter((internalName) => {
        if (!notificationContent[internalName]) return false;
        const offerPref = offersDeclinedMap[internalName];
        return (
          !user?.accountPreferences.find(
            (pref) => pref.preferenceName === offerPref,
          )?.preferenceValue && !state.dismissed.includes(internalName)
        );
      })
      .map((internalName) => {
        const content = notificationContent[internalName];
        return {
          id: internalName,
          title: content.title,
          description: content.description,
          policyIds: [],
          actionLabel: content.buttonText,
          actionUrl: content.path,
          read: state.read.includes(internalName),
        };
      });

    return output.concat(offerNotifications);
  }, [
    user?.accountPreferences,
    eligibleOffers.unenrolledOffers,
    offersError,
    uiEvents?.events.notifications,
    uiEventsLoading,
    ssNotificationData,
  ]);

  return { notifications, isLoading };
}

export type NotificationItems = {
  notifications: SSNotification[];
  total: number;
  unread: number;
  isLoading: boolean;
  markRead: (notifications: SSNotification[]) => void;
  dismiss: (notifications: SSNotification[]) => void;
  openDrawer: () => void;
  /** Resolves once the mark-read write (if any) has settled. */
  closeDrawer: () => Promise<void>;
  isDrawerOpen: boolean;
};

const notificationIds = (notifications: SSNotification[]) =>
  notifications.map((notification) => notification.id);

/**
 * The single owner of notification state: derives the notification list, writes
 * read/dismissed state, and controls the drawer.
 *
 * Call this exactly once, from `NotificationProvider`; consumers read the result
 * through `useNotifications`. A second caller would get its own copy of the
 * "navigating away closes the drawer" effect below, and on a single navigation
 * each copy would fire its own `markRead` PATCH and analytics event.
 */
export const useNotificationsController = (): NotificationItems => {
  const { pathname } = useLocation();
  const { notificationPilot } = useFlags();
  // The path the drawer was opened on, so that navigating away can close it.
  const [drawerPath, setDrawerPath] = useState<string | null>(null);
  const selectedPolicyId = usePolicyId();
  const { notifications, isLoading } = useNotificationsData();
  const { trigger } = useMutateNotificationUiEvents();

  const markRead = useCallback(
    (notifications: SSNotification[]) => {
      return trigger({
        read: notificationIds(notifications),
        dismissed: [],
      });
    },
    [trigger],
  );

  const dismiss = useCallback(
    (notifications: SSNotification[]) => {
      return trigger({
        read: [],
        dismissed: notificationIds(notifications),
      });
    },
    [trigger],
  );

  const openDrawer = useCallback(() => {
    trackEvent("Notification drawer opened");
    if (notifications.length > 0) {
      trackEvent("Notifications viewed", {
        notificationIds: notifications.map((notification) => notification.id),
        count: notifications.length,
      });
    }
    setDrawerPath(pathname);
  }, [notifications, pathname]);

  const closeDrawer = useCallback(async () => {
    const unreadNotifications = notifications.filter(
      (notification) => !notification.read,
    );
    setDrawerPath(null);
    if (unreadNotifications.length) {
      trackEvent("Notification marked read", {
        notificationIds: unreadNotifications.map(
          (notification) => notification.id,
        ),
      });
      // The write queue logs and swallows errors, so this never rejects.
      await markRead(unreadNotifications);
    }
  }, [notifications, markRead]);

  // Navigating away closes the drawer.
  useEffect(() => {
    if (drawerPath !== null && drawerPath !== pathname) {
      closeDrawer();
    }
  }, [pathname, drawerPath, closeDrawer]);

  // Hold the count at 0 until read/dismissed state loads, so the badge
  // doesn't briefly show every notification as unread.
  const unread = isLoading
    ? 0
    : notifications.filter((notification) => !notification.read).length;

  const policyRef = useRef<string | undefined | null>(null);

  const totalNotifications = notifications.length;
  const readNotifications = notifications
    .filter((notification) => notification.read)
    .map((notification) => notification.id);
  const unreadNotifications = notifications
    .filter((notification) => !notification.read)
    .map((notification) => notification.id);

  useEffect(() => {
    // unread changes up to twice after the initial load
    // (drawer close -> markRead -> unread settles to 0),
    // policyRef looks to prevent the trackEvent from re-firing since selectedPolicyId hasn't changed.
    // once we change the policyId & new notifications load then we will send another trackevent
    if (
      !notificationPilot ||
      isLoading ||
      policyRef.current === selectedPolicyId
    ) {
      return;
    }

    policyRef.current = selectedPolicyId;
    const metadata = {
      unread,
      totalNotifications,
      readNotifications,
      unreadNotifications,
    };
    trackEvent("Notification Bell Shown", metadata);
  }, [
    notificationPilot,
    selectedPolicyId,
    isLoading,
    unread,
    totalNotifications,
    readNotifications,
    unreadNotifications,
  ]);

  return {
    notifications,
    total: notifications.length,
    unread,
    isLoading,
    markRead,
    dismiss,
    openDrawer,
    closeDrawer,
    isDrawerOpen: drawerPath === pathname,
  };
};
